import { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { getPaymentStatus } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { CheckCircle2, XCircle, Clock, Loader2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(() => !!sessionId);
  const [polling, setPolling] = useState(() => !!sessionId);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) return;

    const maxAttempts = 5;
    const pollInterval = 2000;
    let timeoutId;

    const checkStatus = async () => {
      if (attemptsRef.current >= maxAttempts) {
        setPolling(false);
        setLoading(false);
        return;
      }

      try {
        const data = await getPaymentStatus(sessionId);
        setStatus(data);

        if (data.payment_status === 'paid') {
          setPolling(false);
          setLoading(false);
          toast.success('Paiement réussi !');
          return;
        } else if (data.status === 'expired') {
          setPolling(false);
          setLoading(false);
          toast.error('Session de paiement expirée');
          return;
        }

        attemptsRef.current += 1;
        timeoutId = setTimeout(checkStatus, pollInterval);
      } catch (error) {
        console.error('Error checking payment status:', error);
        setPolling(false);
        setLoading(false);
      }
    };

    checkStatus();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [sessionId]);

  const getStatusDisplay = () => {
    if (loading || polling) {
      return {
        icon: <Loader2 className="w-16 h-16 text-orange-500 animate-spin" />,
        title: 'Vérification du paiement...',
        description: 'Nous vérifions le statut de votre paiement.',
        color: 'text-orange-500'
      };
    }

    if (status?.payment_status === 'paid') {
      return {
        icon: <CheckCircle2 className="w-16 h-16 text-emerald-500" />,
        title: 'Paiement réussi !',
        description: 'Votre paiement a été traité avec succès. La mission est maintenant en cours.',
        color: 'text-emerald-500'
      };
    }

    if (status?.status === 'expired') {
      return {
        icon: <XCircle className="w-16 h-16 text-red-500" />,
        title: 'Session expirée',
        description: 'La session de paiement a expiré. Veuillez réessayer.',
        color: 'text-red-500'
      };
    }

    return {
      icon: <Clock className="w-16 h-16 text-amber-500" />,
      title: 'Paiement en attente',
      description: 'Le paiement est en cours de traitement.',
      color: 'text-amber-500'
    };
  };

  const statusDisplay = getStatusDisplay();

  return (
    <Layout>
      <div className="max-w-lg mx-auto py-12" data-testid="payment-success-page">
        <Card>
          <CardContent className="p-8 text-center">
            <div className="mb-6">
              {statusDisplay.icon}
            </div>
            <h1 className={`text-2xl font-bold font-['Chivo'] mb-2 ${statusDisplay.color}`}>
              {statusDisplay.title}
            </h1>
            <p className="text-slate-600 mb-6">
              {statusDisplay.description}
            </p>

            {status && (
              <div className="bg-slate-50 rounded-lg p-4 mb-6 text-left">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Montant</span>
                    <p className="font-['JetBrains_Mono'] font-semibold">
                      {status.amount_total?.toFixed(2)} {status.currency?.toUpperCase()}
                    </p>
                  </div>
                  <div>
                    <span className="text-slate-500">Statut</span>
                    <p>
                      <Badge className={
                        status.payment_status === 'paid' 
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-amber-100 text-amber-700'
                      }>
                        {status.payment_status === 'paid' ? 'Payé' : 'En attente'}
                      </Badge>
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-col gap-3">
              <Link to="/dashboard">
                <Button className="w-full bg-orange-500 hover:bg-orange-600">
                  Retour au tableau de bord
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/missions">
                <Button variant="outline" className="w-full">
                  Voir mes missions
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default PaymentSuccessPage;
